import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CreateDocumentRevisionForm, RevisionName } from '../types';
import RevisionForm from './forms/RevisionForm';
import AuxiliaryFilesWidget, { AuxFileDraft } from './forms/AuxiliaryFilesWidget';
import {
  RevisionFormValues,
  buildDefaultRevisionFormValues,
  getRevisionFormMissingFields,
  labelsForDocumentFields,
} from './forms/formTypes';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';
import { uploadAuxDraftsForRevision } from './forms/workflowUtils';
import projectsService, { AuxFile } from '../services/projectsService';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

export interface RevisionDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  documentId: number;
  documentTitle: string;
  revisionNames: RevisionName[];
  onSubmit: (
    revision: Pick<
      CreateDocumentRevisionForm,
      'revision_name_id' | 'step' | 'start_date' | 'expected_issue_date'
    >
  ) => Promise<number | void>;
  loading?: boolean;
  nextRevisionCounter?: number;
  initialRevision?: Partial<RevisionFormValues> | null;
  editingRevisionId?: number | null;
  dialogTitle?: string;
  submitLabel?: string;
  submittingLabel?: string;
  lockIdentity?: boolean;
  lockDates?: boolean;
  disabled?: boolean;
}

const RevisionDialog: React.FC<RevisionDialogProps> = ({
  open,
  onClose,
  projectId,
  documentId,
  documentTitle,
  revisionNames,
  onSubmit,
  loading = false,
  nextRevisionCounter,
  initialRevision = null,
  editingRevisionId = null,
  dialogTitle = 'Create Document Revision',
  submitLabel = 'Create Revision',
  submittingLabel = 'Creating...',
  lockIdentity = false,
  lockDates = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [revisionForm, setRevisionForm] = useState<RevisionFormValues>(buildDefaultRevisionFormValues());
  const fieldErrors = useFormFieldErrors();
  const [auxNewEntries, setAuxNewEntries] = useState<AuxFileDraft[]>([]);
  const [existingAuxFiles, setExistingAuxFiles] = useState<AuxFile[]>([]);
  const [loadingExistingAux, setLoadingExistingAux] = useState(false);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadExistingAuxFiles = useCallback(async () => {
    if (!editingRevisionId) {
      setExistingAuxFiles([]);
      return;
    }
    setLoadingExistingAux(true);
    try {
      const result = await projectsService.listRevisionAuxFiles(
        projectId,
        documentId,
        editingRevisionId
      );
      setExistingAuxFiles(result.files);
    } catch (err) {
      console.error('Failed to load auxiliary files', err);
      setExistingAuxFiles([]);
    } finally {
      setLoadingExistingAux(false);
    }
  }, [projectId, documentId, editingRevisionId]);

  useEffect(() => {
    if (open) {
      setRevisionForm({
        ...buildDefaultRevisionFormValues(),
        ...initialRevision,
      });
      setAuxNewEntries([]);
      setPartialError(null);
      void loadExistingAuxFiles();
    }
  }, [open, initialRevision, loadExistingAuxFiles]);

  const busy = loading || submitting;

  const handleDeleteExisting = async (fileId: number) => {
    if (!editingRevisionId) return;
    try {
      await projectsService.deleteRevisionAuxFile(
        projectId,
        documentId,
        editingRevisionId,
        fileId
      );
      setExistingAuxFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err: any) {
      setPartialError(err.response?.data?.error || err.message || 'Failed to delete file');
    }
  };

  const handleDownloadExisting = async (file: AuxFile) => {
    if (!editingRevisionId) return;
    try {
      await projectsService.downloadRevisionAuxFile(
        projectId,
        documentId,
        editingRevisionId,
        file.id,
        file.filename
      );
    } catch (err: any) {
      setPartialError(err.response?.data?.error || err.message || 'Failed to download file');
    }
  };

  const handleSubmit = async () => {
    if (busy || disabled) return;
    const missing = getRevisionFormMissingFields(revisionForm);
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForDocumentFields(missing));
      return;
    }
    setSubmitting(true);
    setPartialError(null);
    fieldErrors.clear();

    try {
      const revisionId = await onSubmit({
        revision_name_id: revisionForm.revision_name_id,
        step: revisionForm.step,
        start_date: revisionForm.start_date || undefined,
        expected_issue_date: revisionForm.expected_issue_date || undefined,
      });

      const targetRevisionId = revisionId ?? editingRevisionId ?? null;
      if (targetRevisionId && auxNewEntries.length > 0) {
        try {
          await uploadAuxDraftsForRevision(
            projectId,
            documentId,
            targetRevisionId,
            auxNewEntries
          );
        } catch (auxErr: any) {
          const count = auxNewEntries.length;
          const auxMsg =
            auxErr.response?.data?.error ||
            auxErr.message ||
            'Failed to upload auxiliary files';
          setPartialError(`Revision saved. Failed to upload ${count} aux file(s): ${auxMsg}`);
          return;
        }
      }

      onClose();
    } catch {
      // Parent surfaces errors
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: surfaceBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              {dialogTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? '#b0bec5' : '#546e7a',
                fontFamily: 'monospace',
              }}
            >
              {documentTitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        {partialError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPartialError(null)}>
            {partialError}
          </Alert>
        )}
        <RevisionForm
          value={revisionForm}
          onChange={setRevisionForm}
          revisionNames={revisionNames}
          nextRevisionCounter={nextRevisionCounter}
          lockIdentity={lockIdentity}
          lockDates={lockDates}
          invalid={fieldErrors.invalid}
          onFieldEdit={fieldErrors.clearField}
        />
        <AuxiliaryFilesWidget
          newEntries={auxNewEntries}
          onNewEntriesChange={setAuxNewEntries}
          existingFiles={existingAuxFiles}
          onDeleteExisting={editingRevisionId ? handleDeleteExisting : undefined}
          onDownloadExisting={editingRevisionId ? handleDownloadExisting : undefined}
          loadingExisting={loadingExistingAux}
          disabled={disabled || busy}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy || disabled}
          sx={{
            bgcolor: NAUTILUS.sand,
            color: NAUTILUS.deepOcean,
            fontWeight: 600,
            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },
            '&:disabled': { bgcolor: alpha(NAUTILUS.sand, 0.3) },
          }}
        >
          {busy ? (
            <CircularProgress size={20} sx={{ color: NAUTILUS.deepOcean }} />
          ) : (
            submitLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionDialog;
